import React, { useEffect, useRef, createContext, useContext, ReactNode } from 'react';

interface FocusManagerContextType {
  announceToScreenReader: (message: string, priority?: 'polite' | 'assertive') => void;
  trapFocus: (element: HTMLElement) => () => void;
  restoreFocus: () => void;
}

const FocusManagerContext = createContext<FocusManagerContextType | null>(null);

export function useFocusManager() {
  const context = useContext(FocusManagerContext);
  if (!context) {
    throw new Error('useFocusManager must be used within FocusManagerProvider');
  }
  return context;
}

interface FocusManagerProviderProps {
  children: ReactNode;
}

export function FocusManagerProvider({ children }: FocusManagerProviderProps) {
  const lastFocusedElement = useRef<HTMLElement | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create live region for screen reader announcements
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;
    
    document.body.appendChild(liveRegion);
    liveRegionRef.current = liveRegion;

    return () => {
      if (liveRegionRef.current && document.body.contains(liveRegionRef.current)) {
        document.body.removeChild(liveRegionRef.current);
      }
    };
  }, []);

  const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (liveRegionRef.current) {
      liveRegionRef.current.setAttribute('aria-live', priority);
      liveRegionRef.current.textContent = message;
      
      // Clear after announcement to allow repeat announcements
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = '';
        }
      }, 1000);
    }
  };

  const trapFocus = (element: HTMLElement) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

    // Store the currently focused element
    lastFocusedElement.current = document.activeElement as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable?.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable?.focus();
            e.preventDefault();
          }
        }
      }
      
      if (e.key === 'Escape') {
        restoreFocus();
      }
    };

    element.addEventListener('keydown', handleKeyDown);
    firstFocusable?.focus();

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  };

  const restoreFocus = () => {
    if (lastFocusedElement.current) {
      lastFocusedElement.current.focus();
      lastFocusedElement.current = null;
    }
  };

  const value = {
    announceToScreenReader,
    trapFocus,
    restoreFocus
  };

  return (
    <FocusManagerContext.Provider value={value}>
      {children}
    </FocusManagerContext.Provider>
  );
}

/**
 * Hook for managing focus within a component
 */
export function useFocusTrap(isActive: boolean = false) {
  const containerRef = useRef<HTMLElement>(null);
  const { trapFocus } = useFocusManager();
  
  useEffect(() => {
    if (isActive && containerRef.current) {
      const cleanup = trapFocus(containerRef.current);
      return cleanup;
    }
  }, [isActive, trapFocus]);
  
  return containerRef;
}

/**
 * Component for accessible skip links
 */
export function SkipLinks() {
  return (
    <nav className="skip-links" aria-label="Skip to content">
      <a 
        href="#main-content"
        className="skip-link"
        onFocus={(e) => e.target.scrollIntoView()}
      >
        Skip to main content
      </a>
      <a 
        href="#adhd-assistant"
        className="skip-link"
        onFocus={(e) => e.target.scrollIntoView()}
      >
        Skip to ADHD assistant
      </a>
      <a 
        href="#navigation"
        className="skip-link"
        onFocus={(e) => e.target.scrollIntoView()}
      >
        Skip to navigation
      </a>
    </nav>
  );
}

/**
 * Enhanced button component with accessibility features
 */
interface AccessibleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  loadingText?: string;
  describedBy?: string;
}

export function AccessibleButton({
  children,
  variant = 'primary',
  size = 'medium',
  loading = false,
  loadingText = 'Loading...',
  describedBy,
  disabled,
  onClick,
  ...props
}: AccessibleButtonProps) {
  const { announceToScreenReader } = useFocusManager();
  
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) return;
    
    // Announce button action to screen readers
    const buttonText = typeof children === 'string' ? children : 'Button activated';
    announceToScreenReader(`${buttonText} activated`);
    
    onClick?.(e);
  };
  
  return (
    <button
      {...props}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
      aria-describedby={describedBy}
      aria-busy={loading}
      className={`accessible-button ${variant} ${size} ${loading ? 'loading' : ''}`}
      style={{
        minHeight: '44px', // ADHD-friendly touch target
        minWidth: '44px',
        ...props.style
      }}
    >
      {loading ? (
        <>
          <span aria-hidden="true">⏳</span>
          <span>{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * ADHD-friendly progress indicator
 */
interface ProgressIndicatorProps {
  current: number;
  total: number;
  label: string;
  showSteps?: boolean;
  variant?: 'linear' | 'circular';
}

export function ProgressIndicator({ 
  current, 
  total, 
  label, 
  showSteps = true,
  variant = 'linear' 
}: ProgressIndicatorProps) {
  const percentage = Math.round((current / total) * 100);
  const { announceToScreenReader } = useFocusManager();
  
  useEffect(() => {
    // Announce progress milestones
    if (percentage === 25 || percentage === 50 || percentage === 75 || percentage === 100) {
      announceToScreenReader(`${label}: ${percentage}% complete`);
    }
  }, [percentage, label, announceToScreenReader]);
  
  return (
    <div 
      className={`progress-indicator ${variant}`}
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={label}
      aria-describedby={showSteps ? `${label}-steps` : undefined}
    >
      <div className="progress-bar">
        <div 
          className="progress-fill"
          style={{ width: `${percentage}%` }}
          aria-hidden="true"
        />
      </div>
      
      <div className="progress-text">
        <span className="sr-only">{label}: </span>
        {showSteps ? `${current} of ${total}` : `${percentage}%`}
      </div>
      
      {showSteps && (
        <div id={`${label}-steps`} className="progress-steps">
          {Array.from({ length: total }, (_, i) => (
            <div
              key={i}
              className={`step ${i < current ? 'completed' : ''}`}
              aria-label={`Step ${i + 1} ${i < current ? 'completed' : 'pending'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}